import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { LogOut, Lock, CheckCircle2, PlayCircle, Clock, ArrowLeft, GraduationCap, Award } from "lucide-react";
import { QuestionBoard } from "../components/rm/QuestionBoard";

const STATUS_BADGE = {
  not_started: ["Not Started", "bg-slate-100 text-slate-500 border-slate-200"],
  in_progress: ["In Progress", "bg-amber-100 text-amber-800 border-amber-300"],
  completed: ["Completed", "bg-emerald-100 text-emerald-800 border-emerald-300"],
};

export default function RMTraining() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/rm/training")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load your training"))
      .finally(() => setLoading(false));
  }, []);

  const openModule = (m) => {
    if (m.locked) {
      toast.info("Pass the previous module's quiz to unlock this one");
      return;
    }
    navigate(`/rm/training/module/${m.id}`);
  };

  const pct = data ? Math.round((data.completed_count / data.total_modules) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F4F8F8]" data-testid="rm-training-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <p className="text-lg font-extrabold tracking-tight text-[#0A2540]">Clinq <span className="text-[#0A8B85]">RM Portal</span></p>
            <p className="text-xs text-slate-500">Relationship Manager · {user?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to={user?.role === "admin" ? "/admin/rms" : "/rm/dashboard"}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              data-testid="rm-training-back-dashboard">
              <ArrowLeft size={14} /> {user?.role === "admin" ? "Admin" : "My Clients"}
            </Link>
            <button onClick={async () => { await logout(); window.location.assign("/login"); }}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              data-testid="rm-training-logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold text-[#0A2540]" data-testid="rm-training-title">
          <GraduationCap className="text-[#0A8B85]" size={26} /> RM Training
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {data?.total_modules || 9} modules in a fixed sequence. Pass each module's quiz to unlock the next one.
        </p>

        {loading ? (
          <p className="py-12 text-center text-slate-400">Loading…</p>
        ) : !data ? (
          <p className="py-12 text-center text-slate-400">Couldn't load training. Refresh to try again.</p>
        ) : (
          <>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="rm-training-progress-tracker">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Your progress</p>
                  <p className="text-xl font-extrabold text-[#0A2540]" data-testid="rm-training-progress-count">
                    {data.completed_count} of {data.total_modules} modules completed
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Training window</p>
                  <p className="text-xl font-extrabold" data-testid="rm-training-days-remaining">
                    {data.started_at ? (
                      <span className={data.days_remaining <= 5 ? "text-red-600" : "text-[#0A8B85]"}>
                        {data.days_remaining} {data.days_remaining === 1 ? "day" : "days"} remaining
                      </span>
                    ) : (
                      <span className="text-slate-400 text-base">Starts when you open Module 1</span>
                    )}
                  </p>
                  {data.started_at && (
                    <p className="text-[11px] text-slate-400">of your {data.window_days}-day window</p>
                  )}
                </div>
              </div>
              <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-[#0A8B85] transition-all" style={{ width: `${pct}%` }} data-testid="rm-training-progress-bar" />
              </div>
            </div>

            <div className="mt-6 space-y-3" data-testid="rm-training-module-list">
              {data.modules.map((m) => {
                const [label, cls] = STATUS_BADGE[m.status] || STATUS_BADGE.not_started;
                return (
                  <button key={m.id} onClick={() => openModule(m)}
                    className={`flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left transition ${
                      m.locked ? "border-slate-200 opacity-60" : "border-slate-200 hover:border-[#0A8B85] hover:shadow-sm"}`}
                    data-testid={`rm-training-module-${m.id}`}>
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-extrabold ${
                      m.status === "completed" ? "bg-emerald-100 text-emerald-700"
                        : m.locked ? "bg-slate-100 text-slate-400" : "bg-[#0A8B85]/10 text-[#0A8B85]"}`}>
                      {m.status === "completed" ? <CheckCircle2 size={20} /> : m.locked ? <Lock size={16} /> : m.id}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-[#0A2540]">Module {m.id}: {m.title}</p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                        <Clock size={12} /> approximately {m.minutes} minutes
                        {m.quiz_score != null && <span className="ml-2 font-semibold text-[#0A8B85]">Quiz score: {m.quiz_score}%</span>}
                      </p>
                    </div>
                    <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${cls}`}
                      data-testid={`rm-training-module-${m.id}-status`}>{label}</span>
                    {!m.locked && m.status !== "completed" && (
                      <PlayCircle size={18} className="shrink-0 text-[#0A8B85]" />
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-[11px] text-slate-400">
              Your 21-day training clock starts the first time you open Module 1.
            </p>

            {data.certification ? (
              <div className="mt-8 rounded-2xl border-2 border-[#0A8B85] bg-white p-6 text-center" data-testid="rm-certification-display">
                <Award className="mx-auto text-[#0A8B85]" size={34} />
                <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-[#0A8B85]">Clinq Relationship Manager Certification</p>
                <p className="mt-1 text-xl font-extrabold text-[#0A2540]">{data.certification.rm_name}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Certified {data.certification.certified_at?.slice(0, 10)} · Overall score {data.certification.score_pct}%
                </p>
              </div>
            ) : (
              <div className={`mt-8 rounded-2xl border-2 p-6 ${data.final?.unlocked
                ? "border-[#0A2540] bg-white" : "border-dashed border-slate-300 bg-white/60 opacity-70"}`}
                data-testid="rm-final-assessment-card">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-extrabold text-[#0A2540]">
                      <Award size={17} className="text-[#0A8B85]" /> Final Certification Assessment
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {data.final?.questions || 38} questions from all {data.total_modules} modules · pass mark 80% · earns your Clinq Relationship Manager Certification.
                    </p>
                    {data.final?.blocked && (
                      <p className="mt-1 text-xs font-semibold text-red-600" data-testid="rm-final-blocked-note">Two failed attempts — flagged to the Clinq team.</p>
                    )}
                  </div>
                  {data.final?.unlocked ? (
                    <button onClick={() => navigate("/rm/training/final")}
                      className="rounded-xl bg-[#0A2540] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0d2f52]"
                      data-testid="rm-final-start-btn">
                      {data.final.attempts_used > 0 ? "Open assessment" : "Start assessment"}
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-bold text-slate-400">
                      <Lock size={12} /> Pass all {data.total_modules} module quizzes to unlock
                    </span>
                  )}
                </div>
              </div>
            )}

            <QuestionBoard />
          </>
        )}
      </main>
    </div>
  );
}
